import * as THREE from 'three';
import {
	Bone,
	BufferGeometry,
	Camera,
	DirectionalLight,
	Group,
	HemisphereLight,
	Material,
	Mesh,
	Object3D,
	PointLight,
	Scene,
	SkinnedMesh,
	SpotLight,
	Texture, Vector2, Vector3,
} from 'three';
import { Command } from './command.js';
import { History as EditorHistory } from './history.js';
import { PandaMode } from './enums.js';
import { Selector } from './viewport.selector.js';
import { Signal } from './signals.js';
import {Control, PandaOptions,TwinConfig} from './panda.js';
import { AddObjectCommand,CombineCommand,DecombineCommand } from './commands/Commands.js';
import {Loader} from './loader';
import {mergeGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils';
import localforage from 'localforage';
import {ViewEffectComposer} from "./effect";

const _DEFAULT_CAMERA = new THREE.PerspectiveCamera(30, 1, 0.01, 10000);
_DEFAULT_CAMERA.name = '编辑器';
_DEFAULT_CAMERA.position.set(30, 6, 30);
_DEFAULT_CAMERA.lookAt(new THREE.Vector3());

const _ClIPBOARD_KEY='panda_clipboard';

interface SignalEvent {
	editScript: Signal<any>;
	startPlayer: Signal<any>;
	stopPlayer: Signal<any>;

	// vr

	toggleVR: Signal<any>;
	exitedVR: Signal<any>;

	// notifications

	editorCleared: Signal<any>;

	savingStarted: Signal<any>;
	savingFinished: Signal<any>;

	transformModeChanged: Signal<any>;
	snapChanged: Signal<any>;
	spaceChanged: Signal<any>;
	rendererCreated: Signal<any>;
	rendererUpdated: Signal<any>;

	sceneBackgroundChanged: Signal<any>;
	sceneEnvironmentChanged: Signal<any>;
	sceneFogChanged: Signal<any>;
	sceneFogSettingsChanged: Signal<any>;
	sceneGraphChanged: Signal<any>;
	sceneRendered: Signal<any>;

	cameraChanged: Signal<any>;
	cameraResetted: Signal<any>;

	geometryChanged: Signal<any>;

	objectSelected: Signal<any>;
	objectDblclick: Signal<any>;
	objectContextmenu : Signal<any>;
	objectMouseIn: Signal<any>;
	objectMouseOut: Signal<any>;

	objectFocused: Signal<any>;

	objectAdded: Signal<any>;
	objectChanged: Signal<any>;
	objectRemoved: Signal<any>;

	cameraAdded: Signal<any>;
	cameraRemoved: Signal<any>;

	helperAdded: Signal<any>;
	helperRemoved: Signal<any>;

	materialAdded: Signal<any>;
	materialChanged: Signal<any>;
	materialRemoved: Signal<any>;

	scriptAdded: Signal<any>;
	scriptChanged: Signal<any>;
	scriptRemoved: Signal<any>;

	windowResize: Signal<any>;

	showGridChanged: Signal<any>;
	showHelpersChanged: Signal<any>;
	refreshSidebarObject3D: Signal<any>;
	historyChanged: Signal<any>;

	viewportCameraChanged: Signal<any>;
	viewportShadingChanged: Signal<any>;

	intersectionsDetected: Signal<any>;
}

export enum EditorTheme{
	System='system',
	Light='light',
	Dark='dark'
}

export interface CameraView {
	name:string,
	duration: number
	position: any
 }
 
class Editor {

	theme:EditorTheme=EditorTheme.System;

	signals: SignalEvent;
	selector: Selector;
	history: EditorHistory;
	group: Group = new THREE.Group();

	loader=new Loader(this);

	camera: Camera = _DEFAULT_CAMERA.clone();
	// 看向摄像头的模型
	lookCameraObj = []
	// 发光的模型
	outlineObj = []
	// 线框的模型
	edgesObj = []
	// 相机多视角
	cameraViews = []

	scene: Scene = new THREE.Scene();
	sceneHelpers: Scene=new THREE.Scene();
	object = {};
	geometries: { [propname: string]: BufferGeometry } = {};
	materials: { [propname: string]: Material } = {};
	textures: { [propname: string]: Texture } = {};
	scripts: { [propname: string]: string[] } = {};

	materialsRefCounter = new Map(); // tracks how often is a material used by a 3D object

	mixer = new THREE.AnimationMixer(this.scene);

	ctrlSelectObject: number[] = [] // 记住多选前每个对象的父节点
	selected: Object3D | null = null;
	helpers: { [propname: string]: Object3D } = {};

	cameras: { [propname: string]: Camera } = {};

	viewportCamera: Camera = this.camera;
	viewportShading = 'default';

	mode: PandaMode = PandaMode.Edit;
	options?: PandaOptions;

	twinConfig?: TwinConfig
	// 效果合成器
	effectComposer: ViewEffectComposer

	constructor(options?: PandaOptions) {
		// const Signal = signals.Signal;
		this.options = options;
		this.signals = {

			// script

			editScript: new Signal(),

			// player

			startPlayer: new Signal(),
			stopPlayer: new Signal(),

			// vr

			toggleVR: new Signal(),
			exitedVR: new Signal(),

			// notifications

			editorCleared: new Signal(),

			savingStarted: new Signal(),
			savingFinished: new Signal(),

			transformModeChanged: new Signal(),
			snapChanged: new Signal(),
			spaceChanged: new Signal(),
			rendererCreated: new Signal(),
			rendererUpdated: new Signal(),

			sceneBackgroundChanged: new Signal(),
			sceneEnvironmentChanged: new Signal(),
			sceneFogChanged: new Signal(),
			sceneFogSettingsChanged: new Signal(),
			sceneGraphChanged: new Signal(),
			sceneRendered: new Signal(),

			cameraChanged: new Signal(),
			cameraResetted: new Signal(),

			geometryChanged: new Signal(),

			objectSelected: new Signal(),
			objectDblclick: new Signal(),
			objectContextmenu: new Signal(),
			objectMouseIn: new Signal(),
			objectMouseOut: new Signal(),
			objectFocused: new Signal(),

			objectAdded: new Signal(),
			objectChanged: new Signal(),
			objectRemoved: new Signal(),

			cameraAdded: new Signal(),
			cameraRemoved: new Signal(),

			helperAdded: new Signal(),
			helperRemoved: new Signal(),

			materialAdded: new Signal(),
			materialChanged: new Signal(),
			materialRemoved: new Signal(),

			scriptAdded: new Signal(),
			scriptChanged: new Signal(),
			scriptRemoved: new Signal(),

			windowResize: new Signal(),

			showGridChanged: new Signal(),
			showHelpersChanged: new Signal(),
			refreshSidebarObject3D: new Signal(),
			historyChanged: new Signal(),

			viewportCameraChanged: new Signal(),
			viewportShadingChanged: new Signal(),

			intersectionsDetected: new Signal(),

		};

		this.group.name = '虚拟组'
		this.group.userData.isMulti = true // 判断是否为多选的依据

		this.selector = new Selector(this);
		this.scene.name = '场景';
		this.addCamera(this.camera);
		this.history = new EditorHistory(this);
	}

	initEffectComposer(domElement:HTMLElement,renderer: THREE.WebGLRenderer){
		if (this.effectComposer && this.effectComposer.effectComposer) {
			this.effectComposer.effectComposer.dispose()
		}
		this.effectComposer = new ViewEffectComposer(this.scene,this.viewportCamera,domElement)
		this.effectComposer.setEffectComposer(renderer)
	}


	setScene(scene: Scene) {
		this.scene.uuid = scene.uuid;
		this.scene.name = scene.name;

		this.scene.background = scene.background;
		this.scene.environment = scene.environment;
		this.scene.fog = scene.fog;
		this.scene.backgroundBlurriness = scene.backgroundBlurriness;
		this.scene.backgroundIntensity = scene.backgroundIntensity;

		this.scene.userData = JSON.parse(JSON.stringify(scene.userData));

		// avoid render per object
		this.signals.sceneGraphChanged.active = false;

		while (scene.children.length > 0) {
			this.addObject(scene.children[0]);
		}

		this.signals.sceneGraphChanged.active = true;

		this.signals.sceneGraphChanged.dispatch(true);

	}

	//

	addObject(object: Object3D, parent?: Object3D, index?: number) {
		// this.outline.addObject(object,parent,index);
		let  scope = this;

		object.traverse(function (child: Object3D) {

			if (child instanceof Mesh) {
				if (child.geometry !== undefined) scope.addGeometry(child.geometry);
				if (child.material !== undefined) scope.addMaterial(child.material);
			}

			if (child instanceof Camera) {
				scope.addCamera(child);
			}
			scope.addHelper(child);

		});

		if (parent === undefined) {

			this.scene.add(object);

		} else if (index != undefined) {

			parent.children.splice(index, 0, object);
			object.parent = parent;

		}

		this.signals.objectAdded.dispatch(object);
		this.signals.sceneGraphChanged.dispatch();

	}

	moveObject(object: Object3D, parent?: Object3D, before?: Object3D) {
		// this.outline.moveObject(object,parent,before);
		if (parent === undefined) {
			parent = this.scene;
		}

		parent.add(object);
		if (before !== undefined) {

			var index = parent.children.indexOf(before);
			parent.children.splice(index, 0, object);
			parent.children.pop();

		}

		this.signals.sceneGraphChanged.dispatch();

	}

	nameObject(object: Object3D, name: string) {
		object.name = name;
		this.signals.sceneGraphChanged.dispatch();

	}

	removeObject(object: Object3D) {

		if (object.parent === null) return; // avoid deleting the camera or scene
		let scope = this;

		object.traverse(function (child) {
			if (child instanceof Camera) {
				scope.removeCamera(child);
			}
			scope.removeHelper(child);

			if (child instanceof Mesh && child.material !== undefined) scope.removeMaterial(child.material);

		});

		object.parent.remove(object);

		this.signals.objectRemoved.dispatch(object);
		this.signals.sceneGraphChanged.dispatch();

	}

	addGeometry(geometry: BufferGeometry) {

		this.geometries[geometry.uuid] = geometry;

	}

	setGeometryName(geometry: BufferGeometry, name: string) {

		geometry.name = name;
		this.signals.sceneGraphChanged.dispatch();

	}

	addMaterial(material: Material) {

		if (Array.isArray(material)) {

			for (let i = 0, l = material.length; i < l; i++) {
				this.addMaterialToRefCounter(material[i]);
			}
		} else {

			this.addMaterialToRefCounter(material);

		}

		this.signals.materialAdded.dispatch();

	}

	addMaterialToRefCounter(material: Material) {

		let materialsRefCounter = this.materialsRefCounter;
		let count = materialsRefCounter.get(material);

		if (count === undefined) {

			materialsRefCounter.set(material, 1);
			this.materials[material.uuid] = material;

		} else {

			count++;
			materialsRefCounter.set(material, count);

		}

	}

	removeMaterial(material: Material) {

		if (Array.isArray(material)) {
			for (let i = 0, l = material.length; i < l; i++) {
				this.removeMaterialFromRefCounter(material[i]);
			}
		} else {
			this.removeMaterialFromRefCounter(material);
		}

		this.signals.materialRemoved.dispatch();

	}

	removeMaterialFromRefCounter(material: Material) {

		let materialsRefCounter = this.materialsRefCounter;
		let count = materialsRefCounter.get(material);
		count--;

		if (count === 0) {
			materialsRefCounter.delete(material);
			delete this.materials[material.uuid];
		} else {
			materialsRefCounter.set(material, count);
		}

	}

	getMaterialById(id: number) {

		let material;
		let materials = Object.values(this.materials);

		for (var i = 0; i < materials.length; i++) {

			if (materials[i].id === id) {

				material = materials[i];
				break;

			}

		}

		return material;

	}

	setMaterialName(material: Material, name: string) {

		material.name = name;
		this.signals.sceneGraphChanged.dispatch();

	}

	addTexture(texture: Texture) {

		this.textures[texture.uuid] = texture;

	}

	addCamera(camera: Camera) {
		if (camera.isCamera) {
			this.cameras[camera.uuid] = camera;
			this.signals.cameraAdded.dispatch(camera);
		}
	}

	removeCamera(camera: Camera) {
		if (this.cameras[camera.uuid] !== undefined) {
			delete this.cameras[camera.uuid];
			this.signals.cameraRemoved.dispatch(camera);
		}
	}

	addHelper(object: Object3D, helper?: Object3D) {
		if (this.mode !== PandaMode.Edit) return;
		let geometry = new THREE.SphereGeometry(2, 4, 2);
		let material = new THREE.MeshBasicMaterial({ color: 0xff0000, visible: false });

		if (helper === undefined) {

			if (object instanceof Camera) {

				helper = new THREE.CameraHelper(object);

			} else if (object instanceof PointLight) {

				helper = new THREE.PointLightHelper(object, 1);

			} else if (object instanceof DirectionalLight) {

				helper = new THREE.DirectionalLightHelper(object, 1);

			} else if (object instanceof SpotLight) {

				helper = new THREE.SpotLightHelper(object);

			} else if (object instanceof HemisphereLight) {

				helper = new THREE.HemisphereLightHelper(object, 1);

			} else if (object instanceof SkinnedMesh) {

				helper = new THREE.SkeletonHelper(object.skeleton.bones[0]);

			} else if (object instanceof Bone && !(object.parent instanceof Bone)) {
				helper = new THREE.SkeletonHelper(object);

			} else {

				// no helper for this object type
				return;

			}

			const picker = new THREE.Mesh(geometry, material);
			picker.name = 'picker';
			picker.userData.object = object;
			helper.add(picker);

		}

		if (this.sceneHelpers) {
			this.sceneHelpers.add(helper);
			this.helpers[object.id] = helper;

			this.signals.helperAdded.dispatch(helper);
		}


	}

	removeHelper(object: Object3D) {

		if (this.helpers[object.id] !== undefined) {

			let helper = this.helpers[object.id];
			helper.parent?.remove(helper);

			delete this.helpers[object.id];

			this.signals.helperRemoved.dispatch(helper);

		}

	}

	addScript(object: Object3D, script: string) {

		if (this.scripts[object.uuid] === undefined) {

			this.scripts[object.uuid] = [];

		}

		this.scripts[object.uuid].push(script);

		this.signals.scriptAdded.dispatch(script);

	}

	removeScript(object: Object3D, script: string) {

		if (this.scripts[object.uuid] === undefined) return;

		let index = this.scripts[object.uuid].indexOf(script);

		if (index !== - 1) {
			this.scripts[object.uuid].splice(index, 1);
		}
		this.signals.scriptRemoved.dispatch(script);

	}

	getObjectMaterial(object: Mesh, slot: number): Material {

		let material = object.material;

		if (Array.isArray(material) && slot !== undefined) {

			material = material[slot];

		}

		return material as Material;

	}

	setObjectMaterial(object: Mesh, slot: number, newMaterial: Material) {

		if (Array.isArray(object.material) && slot !== undefined) {

			object.material[slot] = newMaterial;

		} else {

			object.material = newMaterial;

		}

	}

	setViewportCamera(uuid: string) {
		this.viewportCamera = this.cameras[uuid];
		this.signals.viewportCameraChanged.dispatch();
	}

	setViewportShading(value: string) {
		this.viewportShading = value;
		this.signals.viewportShadingChanged.dispatch();
	}

	//

	select(object?: Object3D) {

		this.selector.select(object);

	}

	selectAll(objects: Object3D[]) {
		let length = objects.length
		this.cancelMulti()
		if(length === 0){
			return
		}
		if(length === 1){
			this.select(objects[0])
			return
		}
		// 取中间点作为原点
		let xArray = [], yArray = [], zArray = [];
		objects.forEach(item=>{
			xArray.push(item.position.x)
			yArray.push(item.position.y)
			zArray.push(item.position.z)
		})
		let x = (Math.max(...xArray) + Math.min(...xArray)) / 2,
			y = (Math.max(...yArray) + Math.min(...yArray)) / 2,
			z = (Math.max(...zArray) + Math.min(...zArray)) / 2;
		// 减去原点的偏差值
		for(let i = length - 1; i >= 0; i--){
			let item = objects[i]
			item.position.set( item.position.x - x, item.position.y - y, item.position.z - z )
			this.group.add(item)
		}
		this.group.position.set(x, y, z)
		//将组添加到画布
		this.scene.add(this.group)
		this.signals.objectSelected.dispatch(this.group)
	}
	cancelMulti(){
		if (!this.group){
			return
		}
		let length = this.group.children.length
		for(let i = length - 1; i >= 0; i--){
			let item = this.group.children[i]
			let x = this.group.position.x + item.position.x
			let y = this.group.position.y + item.position.y
			let z = this.group.position.z + item.position.z
			item.position.set(x, y, z)
			if(this.ctrlSelectObject[item.id] !== undefined){
				this.scene.getObjectById(this.ctrlSelectObject[item.id]).add(item)
			}else{
				//this.group.parent.add(item)
				this.scene.add(item)
			}
		}
		this.group.scale.set(1, 1, 1)
		this.group.children = []
		this.group.parent = this.scene;

		//将组从画布移出
		this.scene.remove(this.group)
	}
	selectById(id: number) {
		if (id === this.camera.id) {
			this.select(this.camera);
			return;
		}
		const object = this.scene.getObjectById(id);
		this.select(object);

	}

	selectByUuid(uuid: string) {

		var scope = this;

		this.scene.traverse(function (child) {

			if (child.uuid === uuid) {

				scope.select(child);

			}

		});

	}

	deselect() {

		this.selector.deselect();

	}

	focus(object?: Object3D) {

		if (object !== undefined) {

			this.signals.objectFocused.dispatch(object);

		}

	}

	focusById(id: number) {

		this.focus(this.scene.getObjectById(id));

	}

	clear() {
		this.history.clear();

		this.camera.copy(_DEFAULT_CAMERA);
		this.signals.cameraResetted.dispatch();

		this.scene.name = '场景';
		this.scene.userData = {};
		this.scene.background = null;
		this.scene.environment = null;
		this.scene.fog = null;

		var objects = this.scene.children;

		while (objects.length > 0) {
			this.removeObject(objects[0]);
		}

		this.geometries = {};
		this.materials = {};
		this.textures = {};
		this.scripts = {};
		this.edgesObj = [];
		this.outlineObj = []
		this.lookCameraObj = []

		this.materialsRefCounter.clear();

		// this.animations = {};
		this.mixer.stopAllAction();
		this.mixer.update(0)

		this.deselect();

		this.signals.editorCleared.dispatch();

	}

	objLookCamera(scene:Scene,camera:Camera){
		this.lookCameraObj.forEach(item=>{
			if (item.type === "horizontal"){
				let object = scene.getObjectByProperty('uuid',item.object)
				if(object){
					const lookAtVector = new Vector3(camera.position.x,0,camera.position.z);
					object.lookAt(lookAtVector)
				}
				
			}
			if (item.type === "vertical"){
				let object = scene.getObjectByProperty('uuid',item.object)
				if(object){
					object.lookAt(camera.position)
				}
			}
		})
	}
	// 设置发光模型
	objOutline(scene:Scene,effectComposer: ViewEffectComposer){
		this.outlineObj.forEach(item=>{
			let object = scene.getObjectByProperty('uuid',item)
			effectComposer.addOutlinePass(object)
		})
	}
	// 设置边框
	objEdge(scene:Scene){
		this.edgesObj.forEach(item=>{
			let object = scene.getObjectByProperty('uuid',item.object)

			if (object instanceof THREE.Group){
				object.traverse(function(child) {
				  if (child instanceof THREE.Mesh) {
					let edgesGeometry = new THREE.EdgesGeometry(child.geometry);
					let edgeMaterial = new THREE.LineBasicMaterial(item.param);
					let edgesMesh = new THREE.LineSegments(edgesGeometry, edgeMaterial);
					child.add(edgesMesh);
				  }
				});
			  }
			  if (object instanceof THREE.Mesh) {
				let edgesGeometry = new THREE.EdgesGeometry(object.geometry);
				let edgeMaterial = new THREE.LineBasicMaterial(item.param);
				let edgesMesh = new THREE.LineSegments(edgesGeometry, edgeMaterial);
				object.add(edgesMesh);
			  }
		})
	}

	async fromJSON(json: any) {
		this.clear();
		let loader = new THREE.ObjectLoader();
		let camera: Camera = (await loader.parseAsync(json.camera)) as Camera;

		this.camera.copy(camera);
		this.signals.cameraResetted.dispatch();
		
		this.scripts = json.scripts;
		this.options= json.options;
		this.twinConfig= json.twinConfig;
		this.edgesObj= json.edgesObj;
		this.outlineObj= json.outlineObj;
		this.lookCameraObj = json.lookCameraObj;
		this.cameraViews = json.cameraViews

		this.setScene((await loader.parseAsync(json.scene)) as Scene);
	}

	toJSON() {
		let scene = this.scene;
		let scripts = this.scripts;

		for (let key in scripts) {
			let script = scripts[key];
			if (script.length === 0 || scene.getObjectByProperty('uuid', key) === undefined) {
				delete scripts[key];
			}
		}

		return {
			metadata: {
				version: '0.0.1'
			},
			options: this.options||{},
			camera: this.viewportCamera.toJSON(),
			scene: this.scene.toJSON(),
			scripts: this.scripts,
			twinConfig: this.twinConfig,
			cameraViews: this.cameraViews,
			edgesObj: this.edgesObj,
			outlineObj: this.outlineObj,
			lookCameraObj: this.lookCameraObj
		};

	}

	objectByUuid(uuid: string) {

		return this.scene.getObjectByProperty('uuid', uuid);

	}

	execute(cmd: Command, optionalName?: string) {

		this.history.execute(cmd, optionalName);

	}

	undo() {
		this.history.undo();
	}

	redo() {

		this.history.redo();

	}

	copy(object?:Object3D){
    return new Promise((resolve,reject)=>{
      const json=(object||this.selected)?.toJSON();
			const text=JSON.stringify(json,(key, value) => {
				if(key==='repeat'&&value===Infinity){
					return -1;
				}
        return value;
      },0)
			// window.localStorage.setItem(_ClIPBOARD_KEY,text);
			localforage.setItem(_ClIPBOARD_KEY,text);
      navigator.clipboard.writeText(text).then(resolve).catch(reject);
    });
  }

  paste(){
    const loader = new THREE.ObjectLoader();
    return new Promise(async (resolve,reject)=>{
      try{
				// let text=window.localStorage.getItem(_ClIPBOARD_KEY);
				var text= await localforage.getItem<string>(_ClIPBOARD_KEY);
				if(text===null){
					text=await navigator.clipboard.readText();
				}
				const jsonObject = JSON.parse(text);
				const object = loader.parse(jsonObject);
				object.uuid=THREE.MathUtils.generateUUID();
				object.name=`${object.name} 复制`;
				if(object instanceof Mesh&&object.userData&&object.userData.isText){
					const mat=object.material.clone();
					mat.uuid=THREE.MathUtils.generateUUID();
					object.material=mat;
				}

        this.execute( new AddObjectCommand( this, object ) );

        resolve(object);
      }catch(e){
        reject(e);
      }

    });
  }

	importFiles(accepts?:string[]){
		const fileInput = document.createElement( 'input' );
		fileInput.multiple = true;
		fileInput.type = 'file';
		fileInput.accept=(accepts||IMPORT_ACCEPTS).join(',');
		fileInput.addEventListener( 'change', ()=> {
			this.loader.loadFiles( fileInput.files );
		} );
		fileInput.click();
	}

	merge(...mesh:Mesh[]):Mesh{
		if(mesh.length<2){
			throw new Error('合并必须多网格');
		}
		let geometryArray = []; // 需要合并的模型数组
  	let materialArray = [];
		let name='';
		mesh.forEach(obj=>{
			obj.updateMatrix();

			const newGeometry = obj.geometry.applyMatrix4(obj.matrix);
			geometryArray.push(newGeometry);
			materialArray.push(obj.material);
			name+=obj.name;
		})
		let merged =mergeGeometries(geometryArray,true);
		let mergeMesh = new THREE.Mesh(merged, materialArray)
		mergeMesh.name=name+' 合并';
		mergeMesh.position.copy(mesh[0].position);
		return mergeMesh;
	}
};

const IMPORT_ACCEPTS=['.json','.dea','.fbx','.glb','.gltf','.bin','.obj','.mtl','.stl','.3mf']

export { Editor };
